import React, { useState, useRef } from "react";
import "../App.css";
import { Button, Form, Row, Col } from "react-bootstrap";
import _ from "lodash";

interface FormItemProps {
  addItem: (text: string, price: number, people: string[]) => void;
  initialName: string;
  initialCost: string;
}

/**
 * Form for adding a new dish. Leverage refs to show pop-up validation messages.
 */
const FormItem: React.FC<FormItemProps> = ({
  addItem,
  initialName,
  initialCost,
}) => {
  const [name, setName] = useState<string>(initialName);
  const [cost, setCost] = useState<string>(initialCost);
  const [people, setPeople] = useState<string>("");
  const [isNameInvalid, setIsNameInvalid] = useState<boolean>(false);
  const [isCostInvalid, setIsCostInvalid] = useState<boolean>(false);
  const [isPeopleInvalid, setIsPeopleInvalid] = useState<boolean>(false);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const costRef = useRef<HTMLInputElement | null>(null);
  const peopleRef = useRef<HTMLInputElement | null>(null);
  const popUpRef = useRef<HTMLDivElement | null>(null);

  // Make a list out of the string input of people's names, separated by comma
  const parsePeople = (peopleString: string): string[] =>
    peopleString.split(",").map((person) => _.capitalize(person.trim()));

  // Helper function to show the pop-up
  const showPopUp = (inputElement: HTMLInputElement | null): void => {
    if (!inputElement || !popUpRef.current) {
      return;
    }

    // Get the position and dimensions of the input element
    const { left, top, width } = inputElement.getBoundingClientRect();

    // Position the pop-up below the input element
    popUpRef.current.style.top = `${
      top + window.pageYOffset + inputElement.offsetHeight
    }px`;
    popUpRef.current.style.left = `${left + window.pageXOffset}px`;
    popUpRef.current.style.width = `${width}px`;
  };

  // Add the item if validation succeeds
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name) {
      setIsNameInvalid(true);
      showPopUp(nameRef.current);
      return;
    }
    setIsNameInvalid(false);

    if (isNaN(parseFloat(cost))) {
      setIsCostInvalid(true);
      showPopUp(costRef.current);
      return;
    }
    setIsCostInvalid(false);

    if (!people) {
      setIsPeopleInvalid(true);
      showPopUp(peopleRef.current);
      return;
    }
    setIsPeopleInvalid(false);

    addItem(name, parseFloat(cost), parsePeople(people));
    setName("");
    setCost("");
    setPeople("");
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="bill-submit-form">
        <Form.Control
          ref={nameRef}
          value={name}
          className={isNameInvalid ? "input-invalid" : "input-valid"}
          onChange={(e) => setName(e.target.value)}
          placeholder="Dish (ex. Ragout Rabbit)"
        />
        {isNameInvalid && (
          <div ref={popUpRef} className="pop-up">
            Can't leave the dish name blank.
          </div>
        )}
        <Form.Control
          ref={costRef}
          value={cost}
          className={isCostInvalid ? "input-invalid" : "input-valid"}
          onChange={(e) => setCost(e.target.value)}
          placeholder="Price (ex. 14.00)"
        />
        {isCostInvalid && (
          <div ref={popUpRef} className="pop-up">
            Cost should be a number without currency symbols (e.g. "23.00", not
            "$23.00").
          </div>
        )}
        <Form.Control
          ref={peopleRef}
          value={people}
          className={isPeopleInvalid ? "input-invalid" : "input-valid"}
          onChange={(e) => setPeople(e.target.value)}
          placeholder="People Having This (ex. Kirito, Asuna)"
        />
        {isPeopleInvalid && (
          <div ref={popUpRef} className="pop-up">
            Please name at least one person having this dish (with multiple
            names separated by comma).
          </div>
        )}
        <div className="mt-2 mb-3">
          <Row>
            <Col className="d-flex">
              <Button
                className="mx-2 modal-button"
                variant="primary"
                type="submit"
              >
                Add
              </Button>
            </Col>
          </Row>
        </div>
      </Form.Group>
    </Form>
  );
};

export default FormItem;
